import type { CollectionViewFields, ImageType, SortDirection } from '@vizit/api';

enum CollectionSortOption {
  DateAdded = 'date_added',
  Alphabetical = 'alphabetical',
}

enum CollectionGridFilterOptions {
  ImageType = 'collectionImageType',
  ProductCategory = 'collectionCategoryId',
  ProductCategoryName = 'collectionCategoryName',
  Search = 'collectionSearchTerm',
  Sort = 'collectionSort',
}

enum CollectionGridActionType {
  SET_PRODUCT_CATEGORY_ID = 'SET_PRODUCT_CATEGORY_ID',
  SET_IMAGE_TYPE = 'SET_IMAGE_TYPE',
  SET_SEARCH_TERM = 'SET_SEARCH_TERM',
  SET_SORT_OPTION = 'SET_SORT_OPTION',
}

interface CollectionGridState {
  productCategoryId?: string | null;
  productCategoryName?: string;
  imageType?: ImageType | null;
  searchTerm?: string;
  sortOption?: CollectionSortOption;
}

const DEFAULT_FILTER_STATE: Required<CollectionGridState> = {
  productCategoryId: null,
  productCategoryName: '',
  imageType: null,
  searchTerm: '',
  sortOption: CollectionSortOption.DateAdded,
};

function mapSortOptionToQueryParams(sortOption: CollectionSortOption): {
  sort_by: CollectionViewFields;
  sort_direction: SortDirection;
} {
  if (sortOption === CollectionSortOption.Alphabetical) {
    return { sort_by: 'name', sort_direction: 'asc' };
  }

  return { sort_by: 'created_at', sort_direction: 'desc' };
}

interface CollectionGridQueryParams {
  limit: number;
  sort_by: CollectionViewFields;
  sort_direction: SortDirection;
  search_name?: string;
  product_category_id?: string[];
  image_type?: ImageType;
}

function transformStateToQueryParams(state: CollectionGridState, limit: number): CollectionGridQueryParams {
  const { productCategoryId, imageType, searchTerm, sortOption = DEFAULT_FILTER_STATE.sortOption } = state;
  const { sort_by, sort_direction } = mapSortOptionToQueryParams(sortOption);
  const trimmedSearchTerm = searchTerm?.trim();

  return {
    limit,
    sort_by,
    sort_direction,
    search_name: trimmedSearchTerm ? trimmedSearchTerm : undefined,
    product_category_id: productCategoryId ? [productCategoryId] : undefined,
    image_type: imageType ?? undefined,
  };
}

function collectionGridFilterReducer(
  currentState: CollectionGridState,
  action: { type: CollectionGridActionType; payload: Partial<CollectionGridState> },
) {
  const { productCategoryId, productCategoryName, imageType, searchTerm, sortOption } = action.payload ?? {};

  switch (action.type) {
    case CollectionGridActionType.SET_PRODUCT_CATEGORY_ID:
      return { ...currentState, productCategoryId, productCategoryName };
    case CollectionGridActionType.SET_IMAGE_TYPE:
      return { ...currentState, imageType };
    case CollectionGridActionType.SET_SEARCH_TERM:
      return { ...currentState, searchTerm };
    case CollectionGridActionType.SET_SORT_OPTION:
      return { ...currentState, sortOption };
    default:
      return currentState;
  }
}

export {
  CollectionGridActionType,
  CollectionGridFilterOptions,
  CollectionSortOption,
  collectionGridFilterReducer,
  DEFAULT_FILTER_STATE,
  mapSortOptionToQueryParams,
  transformStateToQueryParams,
};
export type { CollectionGridQueryParams, CollectionGridState };
